import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Form, InputGroup, Button, Spinner, Card, Badge } from 'react-bootstrap';
import { notify } from 'react-notify-toast';
import moment from 'moment';
import { getFirstInstallmentSales } from '../../services/firstInstallmentService';
import DailySalesTimeSeriesChart from './DailySalesTimeSeriesChart';
import './salesMetrics.css';
import { METRIC_SALES_YEAR_RANGE_SIZE } from '../../constants';
import {
  MONTHS_IN_YEAR,
  COLORS,
  UI_TEXT,
  getMonthName,
  getPreviousPeriod,
  buildDailySalesSeries,
  buildMonthlySalesSeries,
  fetchYearlySalesData,
  calculateTotalSales,
  getNewVsUpsellCounts,
  formatMonthYearLabel,
} from './statisticsUtils';



const FirstInstallmentMetrics = () => {
  const [year, setYear] = useState(moment().year());
  const [month, setMonth] = useState(moment().month() + 1);
  const [showDaysWithoutSales, setShowDaysWithoutSales] = useState(true);
  const [compareWithPreviousMonth, setCompareWithPreviousMonth] = useState(false);
  const [compareWithPreviousYear, setCompareWithPreviousYear] = useState(false);
  const [appliedShowDaysWithoutSales, setAppliedShowDaysWithoutSales] = useState(true);
  const [appliedCompareWithPreviousMonth, setAppliedCompareWithPreviousMonth] = useState(false);
  const [appliedCompareWithPreviousYear, setAppliedCompareWithPreviousYear] = useState(false);
  const [data, setData] = useState([]);
  const [previousMonthData, setPreviousMonthData] = useState([]);
  const [previousMonthPeriod, setPreviousMonthPeriod] = useState(null);
  const [yearlyData, setYearlyData] = useState([]);
  const [yearlyPreviousData, setYearlyPreviousData] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const years = Array.from({ length: METRIC_SALES_YEAR_RANGE_SIZE }, (_, index) => moment().year() - index);

  const getTotalSalesFromResponse = (responseData = [], responseMetadata = null) => {
    if (responseMetadata && typeof responseMetadata.totalSales === 'number') {
      return responseMetadata.totalSales;
    }
    return (responseData || []).reduce(
      (accumulatedSales, item) => accumulatedSales + (Number(item.count) || 0),
      0,
    );
  };

  const fetchData = useCallback(async (
    showEmptyDays = appliedShowDaysWithoutSales,
    shouldCompareWithPrevious = appliedCompareWithPreviousMonth,
  ) => {
    setIsLoading(true);
    setError(null);
    try {
      const requests = [getFirstInstallmentSales(year, month)];
      const previousPeriod = getPreviousPeriod(year, month);

      if (shouldCompareWithPrevious) {
        requests.push(getFirstInstallmentSales(previousPeriod.year, previousPeriod.month));
      }

      const [currentResponse, previousResponse] = await Promise.all(requests);

      const normalizedData = showEmptyDays
        ? buildDailySalesSeries(currentResponse.data || [], year, month)
        : currentResponse.data || [];
      setData(normalizedData);

      const currentTotalSales = getTotalSalesFromResponse(currentResponse.data, currentResponse.metadata);
      const currentSplit = getNewVsUpsellCounts(currentResponse.data || [], currentTotalSales, year, month);
      let previousTotalSales = null;
      let previousPeriodLabelForSummary = null;
      let previousNewCustomers = null;
      let previousUpsellCustomers = null;

      if (shouldCompareWithPrevious && previousResponse) {
        const normalizedPreviousData = showEmptyDays
          ? buildDailySalesSeries(previousResponse.data || [], previousPeriod.year, previousPeriod.month)
          : previousResponse.data || [];
        setPreviousMonthData(normalizedPreviousData);
        setPreviousMonthPeriod(previousPeriod);
        previousTotalSales = getTotalSalesFromResponse(previousResponse.data, previousResponse.metadata);
        previousPeriodLabelForSummary = formatMonthYearLabel(previousPeriod.month, previousPeriod.year);
        const previousSplit = getNewVsUpsellCounts(previousResponse.data || [], previousTotalSales, previousPeriod.year, previousPeriod.month);
        previousNewCustomers = previousSplit.newCustomers;
        previousUpsellCustomers = previousSplit.upsellCustomers;
      } else {
        setPreviousMonthData([]);
        setPreviousMonthPeriod(null);
      }

      setMetadata({
        totalSales: currentTotalSales,
        newCustomers: currentSplit.newCustomers,
        upsellCustomers: currentSplit.upsellCustomers,
        previousTotalSales,
        previousNewCustomers,
        previousUpsellCustomers,
        previousPeriodLabel: previousPeriodLabelForSummary,
      });
    } catch (err) {
      const errorMessage = err.message || 'Error al obtener las estadísticas';
      setError(errorMessage);
      notify.show(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [year, month, appliedShowDaysWithoutSales, appliedCompareWithPreviousMonth]);

  const fetchYearlyData = useCallback(async (
    shouldCompareWithPreviousYear = appliedCompareWithPreviousYear,
  ) => {
    setIsLoading(true);
    setError(null);
    try {
      const { monthlyDataMap: currentYearMap, dailyData: currentYearDailyData } = await fetchYearlySalesData(year);
      const currentYearSeries = buildMonthlySalesSeries(currentYearMap, year);
      setYearlyData(currentYearSeries);
      const currentYearTotalSales = calculateTotalSales(currentYearMap);
      const currentYearSplit = getNewVsUpsellCounts(currentYearDailyData, currentYearTotalSales, year, null);

      let previousYearTotalSales = null;
      let previousYearLabel = null;
      let previousYearNewCustomers = null;
      let previousYearUpsellCustomers = null;

      if (shouldCompareWithPreviousYear) {
        const previousYear = year - 1;
        const { monthlyDataMap: previousYearMap, dailyData: previousYearDailyData } = await fetchYearlySalesData(previousYear);
        const previousYearSeries = buildMonthlySalesSeries(previousYearMap, previousYear);
        setYearlyPreviousData(previousYearSeries);
        previousYearTotalSales = calculateTotalSales(previousYearMap);
        previousYearLabel = `${previousYear}`;
        const previousYearSplit = getNewVsUpsellCounts(previousYearDailyData, previousYearTotalSales, previousYear, null);
        previousYearNewCustomers = previousYearSplit.newCustomers;
        previousYearUpsellCustomers = previousYearSplit.upsellCustomers;
      } else {
        setYearlyPreviousData([]);
      }

      setMetadata({
        totalSales: currentYearTotalSales,
        newCustomers: currentYearSplit.newCustomers,
        upsellCustomers: currentYearSplit.upsellCustomers,
        previousTotalSales: previousYearTotalSales,
        previousNewCustomers: previousYearNewCustomers,
        previousUpsellCustomers: previousYearUpsellCustomers,
        previousPeriodLabel: previousYearLabel,
      });
    } catch (err) {
      const errorMessage = err.message || UI_TEXT.yearlyErrorMessage;
      setError(errorMessage);
      notify.show(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [year, appliedCompareWithPreviousYear]);

  const handleUpdate = useCallback(() => {
    setAppliedShowDaysWithoutSales(showDaysWithoutSales);
    setAppliedCompareWithPreviousMonth(compareWithPreviousMonth);
    setAppliedCompareWithPreviousYear(compareWithPreviousYear);
    if (compareWithPreviousYear) {
      fetchYearlyData(compareWithPreviousYear);
    } else {
      fetchData(showDaysWithoutSales, compareWithPreviousMonth);
    }
  }, [compareWithPreviousMonth, compareWithPreviousYear, fetchData, fetchYearlyData, showDaysWithoutSales]);

  const previousMonthLabel = previousMonthPeriod
    ? `${previousMonthPeriod.month}/${previousMonthPeriod.year}`
    : '';

  const summaryCurrentPeriodLabel = appliedCompareWithPreviousYear
    ? `${year}`
    : formatMonthYearLabel(month, year);
  const summaryPreviousPeriodPrefix = appliedCompareWithPreviousYear
    ? 'Año anterior'
    : 'Mes anterior';

  const summaryTotalSales = metadata ? metadata.totalSales || 0 : 0;
  const summaryNewCustomers = metadata && typeof metadata.newCustomers === 'number' ? metadata.newCustomers : 0;
  const summaryUpsellCustomers = metadata && typeof metadata.upsellCustomers === 'number' ? metadata.upsellCustomers : 0;
  const summaryNewPercentage = summaryTotalSales > 0 ? Math.round((summaryNewCustomers / summaryTotalSales) * 100) : 0;
  const summaryUpsellPercentage = summaryTotalSales > 0 ? Math.max(100 - summaryNewPercentage, 0) : 0;
  const hasPreviousSummary = Boolean(metadata && metadata.previousTotalSales !== null && metadata.previousPeriodLabel);
  const summaryTotalDifference = hasPreviousSummary ? summaryTotalSales - metadata.previousTotalSales : 0;
  const summaryPreviousTotal = hasPreviousSummary ? metadata.previousTotalSales : 0;
  const summaryPreviousNew = hasPreviousSummary && typeof metadata.previousNewCustomers === 'number' ? metadata.previousNewCustomers : 0;
  const summaryPreviousUpsell = hasPreviousSummary && typeof metadata.previousUpsellCustomers === 'number' ? metadata.previousUpsellCustomers : 0;

  const hasDataToDisplay = appliedCompareWithPreviousYear
    ? yearlyData.length > 0
    : data.length > 0 || (appliedCompareWithPreviousMonth && previousMonthData.length > 0);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  return (
    <Container className="mb-5 pl-0 pr-0">
      <Container className="bg-light rounded shadow p-3 mb-4">
        <h3 style={{ color: COLORS.primary, fontSize: '1.2rem', marginBottom: '20px' }}>
          {UI_TEXT.statisticsTitle}
        </h3>
        <Form>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <InputGroup>
                  <InputGroup.Prepend>
                    <InputGroup.Text>{UI_TEXT.yearLabel}</InputGroup.Text>
                  </InputGroup.Prepend>
                  <Form.Control
                    as="select"
                    value={year}
                    onChange={(e) => setYear(parseInt(e.target.value))}
                    disabled={isLoading}
                  >
                    {years.map((yearOption) => (
                      <option key={yearOption} value={yearOption}>{yearOption}</option>
                    ))}
                  </Form.Control>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <InputGroup>
                  <InputGroup.Prepend>
                    <InputGroup.Text>{UI_TEXT.monthLabel}</InputGroup.Text>
                  </InputGroup.Prepend>
                  <Form.Control
                    as="select"
                    value={month}
                    onChange={(e) => setMonth(parseInt(e.target.value))}
                    disabled={isLoading}
                  >
                    {Array.from({ length: MONTHS_IN_YEAR }, (_, monthIndex) => {
                      const monthName = getMonthName(monthIndex);
                      return (
                        <option key={monthIndex + 1} value={monthIndex + 1}>
                          {monthName}
                        </option>
                      );
                    })}
                  </Form.Control>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={4} className="d-flex align-items-start">
              <Button
                type="button"
                onClick={handleUpdate}
                disabled={isLoading}
                variant="primary"
              >
                {isLoading ? UI_TEXT.loadingButtonText : UI_TEXT.updateButtonText}
              </Button>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={4} className="d-flex align-items-center">
              <Form.Group
                className="mb-0 w-100 px-3 py-0 rounded border"
                style={{ backgroundColor: COLORS.white }}
              >
                <Form.Check
                  id="show-days-without-sales"
                  type="switch"
                  label={UI_TEXT.showDaysWithoutSalesLabel}
                  checked={showDaysWithoutSales}
                  onChange={(e) => setShowDaysWithoutSales(e.target.checked)}
                  disabled={isLoading || compareWithPreviousYear}
                />
              </Form.Group>
            </Col>
            <Col md={4} className="d-flex align-items-center">
              <Form.Group
                className="mb-0 w-100 px-3 py-0 rounded border"
                style={{ backgroundColor: COLORS.white }}
              >
                <Form.Check
                  id="compare-with-previous-month"
                  type="switch"
                  label={UI_TEXT.comparePreviousMonthLabel}
                  checked={compareWithPreviousMonth}
                  onChange={(e) => setCompareWithPreviousMonth(e.target.checked)}
                  disabled={isLoading || compareWithPreviousYear}
                />
              </Form.Group>
            </Col>
            <Col md={4} className="d-flex align-items-center">
              <Form.Group
                className="mb-0 w-100 px-3 py-0 rounded border"
                style={{ backgroundColor: COLORS.white }}
              >
                <Form.Check
                  id="compare-with-previous-year"
                  type="switch"
                  label={UI_TEXT.comparePreviousYearLabel}
                  checked={compareWithPreviousYear}
                  onChange={(e) => setCompareWithPreviousYear(e.target.checked)}
                  disabled={isLoading}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
        {metadata && (
          <Row className="mb-3">
            <Col>
              <Card className="border-0 shadow-sm sales-summary">
                <Card.Body className="p-3">
                  <div className="d-flex justify-content-between align-items-start flex-wrap mb-3">
                    <div>
                      <div className="sales-summary-eyebrow">{UI_TEXT.summaryPrefix}</div>
                      <div className="sales-summary-period">
                        {summaryTotalSales} ventas de primera cuota en {summaryCurrentPeriodLabel}
                      </div>
                    </div>
                    {hasPreviousSummary && (
                      <Badge pill variant={summaryTotalDifference >= 0 ? 'success' : 'danger'} className="sales-summary-delta">
                        {summaryTotalDifference >= 0 ? `+${summaryTotalDifference}` : summaryTotalDifference} vs {summaryPreviousPeriodPrefix.toLowerCase()} ({metadata.previousPeriodLabel})
                      </Badge>
                    )}
                  </div>
                  <Row>
                    <Col md={4} className="mb-2 mb-md-0">
                      <div className="sales-summary-card sales-summary-card-total">
                        <span className="sales-summary-label">Total</span>
                        <span className="sales-summary-value">{summaryTotalSales}</span>
                        <span className="sales-summary-hint">ventas de primera cuota</span>
                      </div>
                    </Col>
                    <Col md={4} className="mb-2 mb-md-0">
                      <div className="sales-summary-card sales-summary-card-new">
                        <span className="sales-summary-label">Nuevos</span>
                        <span className="sales-summary-value">{summaryNewCustomers}</span>
                        <span className="sales-summary-hint">{summaryNewPercentage}% del total · creados en el período</span>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="sales-summary-card sales-summary-card-upsell">
                        <span className="sales-summary-label">Upsell</span>
                        <span className="sales-summary-value">{summaryUpsellCustomers}</span>
                        <span className="sales-summary-hint">{summaryUpsellPercentage}% del total · clientes previos</span>
                      </div>
                    </Col>
                  </Row>
                  <div className="sales-summary-bar mt-3">
                    <div className="sales-summary-bar-segment sales-summary-bar-new" style={{ width: `${summaryNewPercentage}%` }} />
                    <div className="sales-summary-bar-segment sales-summary-bar-upsell" style={{ width: `${summaryUpsellPercentage}%` }} />
                  </div>
                  <div className="d-flex justify-content-between mt-2 flex-wrap">
                    <small className="text-muted">
                      <span className="sales-summary-dot sales-summary-dot-new" />
                      Nuevos {summaryNewPercentage}%
                    </small>
                    <small className="text-muted">
                      <span className="sales-summary-dot sales-summary-dot-upsell" />
                      Upsell {summaryUpsellPercentage}%
                    </small>
                  </div>
                  {hasPreviousSummary && (
                    <div className="sales-summary-previous mt-2">
                      {summaryPreviousPeriodPrefix} ({metadata.previousPeriodLabel}): {summaryPreviousTotal} ventas · Nuevos {summaryPreviousNew} · Upsell {summaryPreviousUpsell}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
      {isLoading ? (
        <Row className="container d-flex bg-light rounded shadow p-3 align-items-center ml-0 mr-0">
          <Col className="d-flex justify-content-center align-items-center">
            <Spinner animation="border" role="status" size="md" className="ml-3 mr-3 my-auto" variant="info" />
            <p className="m-0 p-0">{UI_TEXT.loadingMessage}</p>
          </Col>
        </Row>
      ) : error ? (
        <Row className="container d-flex bg-light rounded shadow p-3 align-items-center ml-0 mr-0">
          <Col className="d-flex justify-content-center text-danger">
            {error}
          </Col>
        </Row>
      ) : hasDataToDisplay ? (
        <Row className="container d-flex bg-light rounded shadow p-3 ml-0 mr-0">
          <Col>
            {appliedCompareWithPreviousYear ? (
              <>
                <h4 className="text-center mb-4">
                  Ventas de Primera Cuota - {year} vs {year - 1}
                </h4>
                <DailySalesTimeSeriesChart
                  data={yearlyData}
                  previousData={yearlyPreviousData}
                  showPreviousLine={appliedCompareWithPreviousYear}
                  currentLabel={`${year}`}
                  previousLabel={`${year - 1}`}
                  isYearlyView={true}
                />
              </>
            ) : (
              <>
                <h4 className="text-center mb-4">
                  Ventas de Primera Cuota - {formatMonthYearLabel(month, year)}
                  {appliedCompareWithPreviousMonth && previousMonthLabel && ` vs ${previousMonthLabel}`}
                </h4>
                <DailySalesTimeSeriesChart
                  data={data}
                  previousData={appliedCompareWithPreviousMonth ? previousMonthData : []}
                  showPreviousLine={appliedCompareWithPreviousMonth}
                  currentLabel={formatMonthYearLabel(month, year)}
                  previousLabel={previousMonthLabel}
                  isYearlyView={false}
                  year={year}
                  month={month}
                  previousYear={previousMonthPeriod ? previousMonthPeriod.year : null}
                  previousMonth={previousMonthPeriod ? previousMonthPeriod.month : null}
                />
              </>
            )}
          </Col>
        </Row>
      ) : (
        <Row className="container d-flex bg-light rounded shadow p-3 align-items-center ml-0 mr-0">
          <Col className="d-flex justify-content-center text-muted">
            {UI_TEXT.noDataMessage} {formatMonthYearLabel(month, year)}
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default FirstInstallmentMetrics;